/**
 * Shared functions for orient and start tools.
 * Single code path for the task-alignment render (WP-465 slice ③ — the verdict
 * itself is server-computed in the Convex orient view), operating protocol (STA-3),
 * codebase coherence (BET-99 / FEAT-182), and doc-completeness (ENT-65 / FEAT-201).
 */

import { checkAndRender, renderCoherenceGate, type CoherenceSnapshot, type CoherenceVerdict } from "../lib/coherence/index.js";
import { checkDocCompleteness, renderDocCompletenessLines, type CollectionDocFields } from "../lib/coherence/doc-completeness.js";
import { resolveProjectRoot } from "../lib/resolve-project-root.js";

export type { CoherenceSnapshot, CoherenceVerdict } from "../lib/coherence/index.js";
export { renderCoherenceGate } from "../lib/coherence/index.js";

export interface CoherenceResult {
  lines: string[];
  /** null when the gateway was unreachable (visible skip banner rendered in `lines`
   *  instead) — see lib/coherence/index.ts's `checkAndRender`. */
  snapshot: CoherenceSnapshot | null;
}

/**
 * Run codebase coherence check and produce orient/start section lines.
 * Returns null when check can't run at all (e.g. no filesystem access, or too few
 * registries resolved locally — DEC-169: respects orient performance budget via
 * early-exit on resolution failure, before ever touching the gateway).
 *
 * ASYNC (TEN-2781, Lane D S1): the coherence verdict is now a served kernel round trip
 * (`chain.evaluateCoherence`) — see lib/coherence/index.ts's file header. A
 * gateway-unreachable outcome is NOT null; it returns the visible skip banner
 * (never a silent pass).
 */
export async function buildCoherenceSection(projectRoot?: string): Promise<CoherenceResult | null> {
  try {
    const root = projectRoot ?? resolveProjectRoot() ?? process.cwd();
    return await checkAndRender(root);
  } catch (err) {
    if (process.env.DEBUG_COHERENCE) {
      console.debug("[coherence] buildCoherenceSection failed:", err instanceof Error ? err.message : err);
    }
    return null;
  }
}

export { type CollectionDocFields } from "../lib/coherence/doc-completeness.js";

export interface DocCompletenessResult {
  lines: string[];
  errorCount: number;
  warningCount: number;
}

/**
 * Run doc-completeness check against fetched collection docs.
 * ENT-65 / FEAT-201: Replaces the removed cross-registry sync rules.
 * Caller is responsible for fetching collections — this is a pure rendering wrapper.
 */
export function buildDocCompletenessSection(
  collections: CollectionDocFields[],
): DocCompletenessResult {
  const report = checkDocCompleteness(collections);
  const lines = renderDocCompletenessLines(report);
  return { lines, errorCount: report.summary.errors, warningCount: report.summary.warnings };
}

// ─── Task alignment (WP-465 slice ③ — server verdict, connector renders) ─────
//
// The local `runAlignmentCheck` matcher that used to live here MOVED into the
// Convex orient view (convex/lib/gates/taskAlignmentPolicy.ts, behind the
// unified evaluate() seam): both the pb CLI and this MCP server consume the
// SAME server-computed `taskAlignment` verdict from `chain.getOrientView`
// (DEC-1277 §4.1 — one matcher, both surfaces; connectors only render).

/** Structural mirror of the served OrientTaskAlignment payload (TEN-2278: the
 *  connector cannot import convex/, so the shape is mirrored here). */
export interface TaskAlignmentPayload {
  verdict: "aligned" | "misaligned" | "cold-start" | "unavailable";
  disposition: "allow" | "advisory";
  coldStart: boolean;
  matchedEntry: { entryId: string | null; name: string } | null;
  matchSource: "active_work_package" | "task_context" | null;
  activeWorkPackageCount: number;
  candidates: Array<{ entryId: string; name: string; collectionSlug: string; overlapRatio: number }>;
  escapeRoutes: Array<{ kind: "link" | "draft-proposal" | "record-divergence"; instruction: string }>;
  citedRule: { entryId: string | null; name: string } | null;
  wording: string;
}

/**
 * Render the server-computed task-alignment verdict. Pure rendering — the
 * wording, candidates, escape routes, and rule citation all arrive in the
 * payload so every surface shows identical text (no local matching, ever).
 * Returns [] when no verdict was served (e.g. no task on this orient).
 */
export function buildTaskAlignmentLines(
  ta: TaskAlignmentPayload | undefined | null,
): string[] {
  if (!ta) return [];
  const lines: string[] = ["## Alignment Check"];
  lines.push(ta.wording);

  if (ta.candidates.length > 0) {
    lines.push("");
    lines.push("**Candidate authorizing entries:**");
    for (const c of ta.candidates) {
      lines.push(`- \`${c.entryId}\` [${c.collectionSlug}] ${c.name} (${Math.round(c.overlapRatio * 100)}% overlap)`);
    }
  }

  if (ta.escapeRoutes.length > 0) {
    lines.push("");
    lines.push("**Your routes:**");
    for (let i = 0; i < ta.escapeRoutes.length; i++) {
      lines.push(`${i + 1}. **${ta.escapeRoutes[i].kind}** — ${ta.escapeRoutes[i].instruction}`);
    }
  }

  if (ta.verdict === "misaligned" && ta.citedRule) {
    lines.push("");
    lines.push(`_Workspace rule: \`${ta.citedRule.entryId ?? ""}\` ${ta.citedRule.name}._`);
  }

  lines.push("");
  return lines;
}

const CORE_PROTOCOL_BASE = [
  "**Search before proposing.** Before suggesting new features, architecture, or changes, " +
    "search the Chain: `entries action=search query=\"<relevant terms>\"`. Build on what exists.",
  "**Reference by ID.** When discussing a topic that has Chain entries, cite them by entry ID " +
    "(e.g. `DEC-50`, `PRI-3`). This keeps conversations grounded in shared knowledge.",
  "**Check scope.** If the proposed work doesn't fall under an active bet, stop and say so. " +
    "Do not design implementation for out-of-scope work without explicit user go-ahead.",
  "**Capture continuously.** When a decision, tension, insight, or new term surfaces during " +
    "work, capture it as a draft immediately. Don't defer to \"later.\"",
];

const RULE5_WITH_GOVERNANCE =
  "**Validate against governance.** Before proposing any solution, check it against the " +
  "Workspace Governance directives below. If your proposal conflicts with a principle, standard, " +
  "or business rule — stop, name the conflict, and get explicit user confirmation before proceeding.";

const RULE5_COMPACT =
  "**Validate against governance.** Before proposing or building anything, call " +
  "`orient task=\"<your task>\"` to load relevant governance. If your proposal conflicts with a " +
  "principle, standard, or business rule — stop, name the conflict, and get explicit user confirmation.";

export interface StandardEntry {
  entryId?: string | null;
  name: string;
  description?: string;
  /**
   * DEC-446: Relevance tier from backend.
   * No-task scheme:  1=org,  2=domain,  3=contextual/fallback.
   * With-task scheme: 1=governs-task-seed, 2=domain+governs, 3=org+governs, 4=fallback.
   * (Tier meaning shifts between schemes — see governanceRanking.ts `tierOf`.)
   */
  tier?: number;
  /** Phase 4 (DEC-1141): authority provenance tier for active governance entries. */
  provenance?: 'authoritative' | 'provisional' | 'floor';
  /** Phase 4 (DEC-1142): team name when anchor resolves — render hint, never a gate. */
  anchoredBy?: string;
}

/**
 * Phase 4 (DEC-1141): compact single-char provenance marker.
 * ★ = authoritative (ratified domain), ~ = provisional (live anchor, not yet ratified),
 * · = floor (pre-Phase-2 legacy, no anchor). Empty string when provenance is absent.
 * Mirrors the CLI's provenanceMark — same vocabulary for consistent UX across surfaces.
 */
export function provenanceMark(p?: string): string {
  if (p === 'authoritative') return '★ ';
  if (p === 'provisional') return '~ ';
  if (p === 'floor') return '· ';
  return '';
}

export interface GovernanceEntries {
  principles?: StandardEntry[];
  standards?: StandardEntry[];
  businessRules?: StandardEntry[];
}

/** A work-packages entry rendered as an active bet in the "current scope" list. */
export interface ActiveBet {
  entryId?: string | null;
  name: string;
}

const MAX_ENTRIES_NO_TASK = 3;
const MAX_ENTRIES_WITH_TASK = 5;

// NOTE: the keyword extractor that used to live here moved server-side with the
// matcher (convex/lib/gates/taskAlignmentPolicy.ts extractTaskKeywords).

function formatGovernanceEntry(entry: StandardEntry): string {
  const id = entry.entryId ?? entry.name;
  const desc = entry.description
    ? ` — ${entry.description.slice(0, 120)}${entry.description.length > 120 ? "..." : ""}`
    : "";
  // DEC-446 / TEN-2383: tier label. Tier meanings differ by scheme (see StandardEntry JSDoc).
  // tier 1 = direct (governs-seed under task, or org in no-task)
  // tier 2 = domain (domain+governs under task, or domain in no-task)
  // tier 3 = org (org+governs under task) or contextual/fallback (no-task)
  const tierLabel = entry.tier === 1 ? " `[direct]`" : entry.tier === 2 ? " `[domain]`" : entry.tier === 3 ? " `[org]`" : "";
  // Phase 4 (DEC-1141): prefix provenance marker and append anchoredBy when present.
  const mark = provenanceMark(entry.provenance);
  const anchorSuffix = entry.anchoredBy ? ` (${entry.anchoredBy})` : "";
  return `- ${mark}**[${id}]** ${entry.name}${anchorSuffix}${tierLabel}${desc}`;
}

/**
 * Build the Operating Protocol section for orient/start output.
 *
 * Two modes:
 * - With governance entries: full mode — rule 5 says "check directives below"
 * - Without governance (compact): rule 5 says "call orient task=... to load governance"
 *
 * Accepts either the new GovernanceEntries object or a plain StandardEntry[]
 * for backward compatibility.
 */
export function buildOperatingProtocol(
  governanceOrStandards?: GovernanceEntries | StandardEntry[],
  task?: string,
): string[] {
  const governance: GovernanceEntries = Array.isArray(governanceOrStandards)
    ? { standards: governanceOrStandards }
    : governanceOrStandards ?? {};

  const { principles = [], standards = [], businessRules = [] } = governance;
  const hasGovernance =
    principles.length > 0 || standards.length > 0 || businessRules.length > 0;

  const rule5 = hasGovernance ? RULE5_WITH_GOVERNANCE : RULE5_COMPACT;
  const protocol = [...CORE_PROTOCOL_BASE, rule5];

  const lines: string[] = [
    "## Operating Protocol",
    "_How to work in this workspace. Follow these before and during every task._",
    "",
  ];

  for (let i = 0; i < protocol.length; i++) {
    lines.push(`${i + 1}. ${protocol[i]}`);
  }

  if (hasGovernance) {
    lines.push("");
    lines.push("### Workspace governance directives");

    const types: Array<{
      header: string;
      entries: StandardEntry[];
    }> = [
      { header: "**Active principles:**", entries: principles },
      { header: "**Active business rules:**", entries: businessRules },
      { header: "**Active standards:**", entries: standards },
    ];

    if (task) {
      // DEC-446 / TEN-2383: Backend provides tier labels for governance sourced via the
      // Convex orient/startup paths. start_pb and other callers may supply governance
      // fetched via chain.listEntries (no tier field). Graceful degradation: per-bucket,
      // tier-sort when tiers are present, otherwise preserve stable insertion order.
      // No throw — a missing tier is NOT a contract violation for the start surface.
      let shownCount = 0;
      let totalGovernance = 0;
      let anyTierRanking = false;

      for (const type of types) {
        if (type.entries.length === 0) continue;
        totalGovernance += type.entries.length;

        // Per-bucket tier check: if ANY entry in this bucket has a tier, sort by tier.
        // Otherwise preserve insertion order (e.g. start_pb governance from listEntries).
        const bucketHasTiers = type.entries.some((e) => e.tier !== undefined);
        if (bucketHasTiers) anyTierRanking = true;
        const sorted = bucketHasTiers
          ? [...type.entries].sort((a, b) => (a.tier ?? 99) - (b.tier ?? 99))
          : [...type.entries];

        const capped = sorted.slice(0, MAX_ENTRIES_WITH_TASK);
        if (capped.length > 0) {
          shownCount += capped.length;
          lines.push(type.header);
          for (const e of capped) {
            lines.push(formatGovernanceEntry(e));
          }
          lines.push("");
        }
      }

      // TEN-2383 / TEN-2431: tier labels arrive from the shared backend ranker (DEC-446) — orient
      // via chain.getOrientView, and start_pb via chain.getBindingGovernanceView (the follow-up TEN-
      // 2431, now landed: start_pb governance routes through the SAME scope-BLIND tiered derivation
      // orient RENDERS, not a hand-rolled chain.listEntries listing-order loop). Domain-scoping the
      // rendered governance is deferred to TEN-2429 (both-surfaces fix). Untiered entries can still
      // arrive from legacy/test callers passing a plain StandardEntry[]; for those this degrades to
      // stable insertion order rather than re-introducing a keyword-relevance "fifth scheme" (the
      // regression PR #214 removed).
      const filterNote = anyTierRanking ? "ranked by relevance" : "in listing order";
      lines.push(
        `_${shownCount} of ${totalGovernance} governance entries shown, ${filterNote}._`,
      );
    } else {
      for (const type of types) {
        if (type.entries.length === 0) continue;
        lines.push(type.header);
        for (const e of type.entries.slice(0, MAX_ENTRIES_NO_TASK)) {
          lines.push(formatGovernanceEntry(e));
        }
        lines.push("");
      }
      lines.push(
        '_Showing top governance entries. Use `orient task="..."` for task-relevant filtering._',
      );
    }
  }

  lines.push("");
  return lines;
}

// ─── BET-126 Slice 2: Hierarchy-aware orient sections ────────────────────────

/** Initiative status item from getOrientEntries (BET-126). */
export interface InitiativeStatusItem {
  id: string;
  entryId: string;
  name: string;
  targetDate: number | null;
  daysRemaining: number | null;
}

/** Workstream health item from getOrientEntries (BET-126). */
export interface WorkstreamHealthItem {
  id: string;
  name: string;
  betCount: number;
  activeBetNames: string[];
}

/** WP-383 S3: Advisory signal — bet with a validates relation to a KR whose outcome verification is overdue. */
export interface OverdueOutcomeBlocker {
  betId: string;
  betName: string;
  krId: string;
  krName: string;
  scheduledVerificationDate: string;
  daysOverdue: number;
}

/** What needs attention from getOrientEntries (BET-126). */
export interface WhatNeedsAttention {
  overdueBets: string[];
  blockedBets: string[];
  criticalPathBets: string[];
  /** WP-383 S3: Advisory — bets whose validates-linked KR verification window has passed. */
  overdueOutcomeBlockers?: OverdueOutcomeBlocker[];
}

/** Constellation entry item from getOrientEntries (BET-126 Slice 3). */
export interface ConstellationEntryItem {
  entryId: string;
  name: string;
  collectionSlug: string;
  canonicalKey: string | null;
}

/**
 * Format Bet Constellation section for full-mode orient (BET-126 Slice 3).
 * Returns empty array if constellationEntries is empty — no empty headers.
 */
export function formatBetConstellationLines(
  constellationEntries: ConstellationEntryItem[] | undefined | null,
  constellationBetName?: string,
): string[] {
  if (!constellationEntries?.length) return [];
  const betName = constellationBetName ?? "Matched bet";
  const lines: string[] = [
    `## Bet Constellation — ${betName}`,
    "_Decisions, tensions, and features related to the matched bet._",
    "",
  ];
  const bySlug = new Map<string, ConstellationEntryItem[]>();
  for (const e of constellationEntries) {
    const list = bySlug.get(e.collectionSlug) ?? [];
    list.push(e);
    bySlug.set(e.collectionSlug, list);
  }
  const order = ["decisions", "tensions", "features"];
  for (const slug of order) {
    const entries = bySlug.get(slug);
    if (!entries?.length) continue;
    const label = slug.charAt(0).toUpperCase() + slug.slice(1);
    lines.push(`**${label}:**`);
    for (const e of entries) {
      lines.push(`- \`${e.entryId}\` — ${e.name}`);
    }
    lines.push("");
  }
  return lines;
}

/**
 * Format Initiative Status section for full-mode orient.
 * Returns empty array if initiatives is empty — no empty headers.
 */
export function formatInitiativeStatusLines(
  initiatives: InitiativeStatusItem[] | undefined | null,
): string[] {
  if (!initiatives?.length) return [];
  const lines: string[] = ["## Initiative Status", ""];
  for (const i of initiatives) {
    const dateStr =
      i.targetDate != null
        ? new Date(i.targetDate).toISOString().split("T")[0]
        : null;
    const daysPart =
      i.daysRemaining != null && dateStr
        ? `${i.daysRemaining} days to ${dateStr}`
        : dateStr
          ? `Target: ${dateStr}`
          : "";
    lines.push(`- \`${i.entryId}\` ${i.name}${daysPart ? ` — ${daysPart}` : ""}`);
  }
  lines.push("");
  return lines;
}

/**
 * Format Workstream Health section for full-mode orient.
 * Returns empty array if workstreams is empty — no empty headers.
 */
export function formatWorkstreamHealthLines(
  workstreams: WorkstreamHealthItem[] | undefined | null,
): string[] {
  if (!workstreams?.length) return [];
  const lines: string[] = ["## Workstream Health", ""];
  for (const ws of workstreams) {
    const betDetail = ws.activeBetNames?.length
      ? ws.activeBetNames.join(", ")
      : `${ws.betCount} bet(s)`;
    lines.push(`- ${ws.name} — ${betDetail}`);
  }
  lines.push("");
  return lines;
}

/**
 * Format What Needs Attention section for full-mode orient.
 * Returns empty array if nothing needs attention — no empty headers.
 */
export function formatWhatNeedsAttentionLines(
  wna: WhatNeedsAttention | undefined | null,
): string[] {
  if (!wna) return [];
  const hasOverdue = (wna.overdueBets?.length ?? 0) > 0;
  const hasBlocked = (wna.blockedBets?.length ?? 0) > 0;
  const hasCritical = (wna.criticalPathBets?.length ?? 0) > 0;
  const hasOutcomeBlockers = (wna.overdueOutcomeBlockers?.length ?? 0) > 0;
  if (!hasOverdue && !hasBlocked && !hasCritical && !hasOutcomeBlockers) return [];
  const lines: string[] = ["## What Needs Attention", ""];
  if (hasOverdue) {
    lines.push("**Overdue:**");
    for (const id of wna.overdueBets ?? []) lines.push(`- \`${id}\``);
    lines.push("");
  }
  if (hasBlocked) {
    lines.push("**Blocked:**");
    for (const id of wna.blockedBets ?? []) lines.push(`- \`${id}\``);
    lines.push("");
  }
  if (hasCritical) {
    lines.push("**Critical path:**");
    for (const id of wna.criticalPathBets ?? []) lines.push(`- \`${id}\``);
    lines.push("");
  }
  if (hasOutcomeBlockers) {
    lines.push("**Overdue outcome verifications (advisory):**");
    for (const b of wna.overdueOutcomeBlockers ?? []) {
      lines.push(`- \`${b.betId}\` → \`${b.krId}\` (${b.daysOverdue}d overdue — ${b.scheduledVerificationDate})`);
    }
    lines.push("");
  }
  return lines;
}

/**
 * Compact "What needs attention" block for brief mode (1–3 bullets: overdue, blocked).
 * Returns empty array if nothing needs attention.
 */
export function formatWhatNeedsAttentionBrief(
  wna: WhatNeedsAttention | undefined | null,
): string[] {
  if (!wna) return [];
  const overdue = wna.overdueBets ?? [];
  const blocked = wna.blockedBets ?? [];
  const critical = wna.criticalPathBets ?? [];
  const outcomeBlockers = wna.overdueOutcomeBlockers ?? [];
  if (overdue.length === 0 && blocked.length === 0 && critical.length === 0 && outcomeBlockers.length === 0) return [];
  const bullets: string[] = [
    ...overdue.slice(0, 2).map((id) => `\`${id}\` (overdue)`),
    ...blocked.slice(0, 2).map((id) => `\`${id}\` (blocked)`),
    ...critical.slice(0, 1).map((id) => `\`${id}\` (critical path)`),
    ...(outcomeBlockers.length > 0
      ? [`${outcomeBlockers.length} bet${outcomeBlockers.length > 1 ? "s" : ""} with overdue outcome verification`]
      : []),
  ].slice(0, 3);
  if (bullets.length === 0) return [];
  const lines: string[] = ["**What needs attention:**", ...bullets.map((b) => `- ${b}`), ""];
  return lines;
}
